/**
 * Hand-rolled SVG data-viz for fleet surfaces. No chart library —
 * deterministic, theme-aware, and tiny.
 */

const GRADE_COLORS: Record<string, string> = {
  F: '#f87171',
  D: '#fb923c',
  C: '#facc15',
  B: '#4ade80',
  A: '#34d399',
};

export function gradeForScore(score: number): string {
  if (score >= 85) return 'A';
  if (score >= 70) return 'B';
  if (score >= 55) return 'C';
  if (score >= 40) return 'D';
  return 'F';
}

// Bucket labels arrive in two shapes ('F' from the org rollup, 'F (0-39)' from
// the fleet overview). Key color + legend off the leading grade letter so the
// bar renders identically wherever it's used.
const gradeKey = (label: string): string => label.trim().charAt(0).toUpperCase();

/**
 * Radial gauge for a 0–100 score. Arc sweeps 270°, colored by grade.
 */
export function RadialGauge({ value, size = 132 }: { value: number; size?: number }) {
  const r = 54;
  const cx = 66;
  const cy = 66;
  const start = 135; // degrees; 270° sweep ends at 45°
  const sweep = 270 * Math.min(1, Math.max(0, value / 100));
  const polar = (deg: number) => {
    const rad = (deg * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
  };
  const [x0, y0] = polar(start);
  const [x1, y1] = polar(start + sweep);
  const [tx, ty] = polar(start + 270);
  const large = sweep > 180 ? 1 : 0;
  const color = GRADE_COLORS[gradeForScore(value)];
  return (
    <svg width={size} height={size} viewBox="0 0 132 132" role="img" aria-label={`score ${value}`}>
      <path
        d={`M ${x0} ${y0} A ${r} ${r} 0 1 1 ${tx} ${ty}`}
        fill="none"
        strokeWidth="10"
        strokeLinecap="round"
        className="stroke-gray-200 dark:stroke-gray-800"
      />
      {sweep > 0 && (
        <path
          d={`M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1}`}
          fill="none"
          stroke={color}
          strokeWidth="10"
          strokeLinecap="round"
        />
      )}
      <text
        x={cx}
        y={cy - 2}
        textAnchor="middle"
        className="fill-gray-900 dark:fill-white"
        style={{ font: '700 28px ui-sans-serif, system-ui', fontVariantNumeric: 'tabular-nums' }}
      >
        {value}
      </text>
      <text
        x={cx}
        y={cy + 20}
        textAnchor="middle"
        style={{ font: '600 13px ui-sans-serif, system-ui' }}
        fill={color}
      >
        grade {gradeForScore(value)}
      </text>
    </svg>
  );
}

/**
 * Composition bar for grade buckets. A stacked spectrum reads honestly
 * under extreme skew (e.g. 68 F / 0 A), where per-bucket bar heights
 * collapse into a single visible block.
 */
export function GradeSpectrum({
  buckets,
  height = 12,
}: {
  buckets: { label: string; count: number }[];
  height?: number;
}) {
  const total = buckets.reduce((a, b) => a + b.count, 0);
  return (
    <div>
      <div
        className="flex w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800"
        style={{ height }}
      >
        {total > 0 &&
          buckets
            .filter((b) => b.count > 0)
            .map((b) => (
              <div
                key={b.label}
                title={`${b.label}: ${b.count}`}
                style={{
                  width: `${(b.count / total) * 100}%`,
                  backgroundColor: GRADE_COLORS[gradeKey(b.label)] ?? '#9ca3af',
                }}
                className="h-full first:rounded-l-full last:rounded-r-full"
              />
            ))}
      </div>
      <div className="mt-2 flex items-center gap-3 flex-wrap">
        {buckets.map((b) => (
          <span
            key={b.label}
            title={b.label}
            className="inline-flex items-center gap-1.5 text-[11px] text-gray-500 dark:text-gray-400"
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: GRADE_COLORS[gradeKey(b.label)] ?? '#9ca3af' }}
            />
            {gradeKey(b.label)}
            <span className="font-semibold tabular-nums text-gray-700 dark:text-gray-300">
              {b.count}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
